import { useState } from 'react';
import { CSS, s } from '../constants/styles';
import { PRESET_SPLITS } from '../constants/presetSplits';
import ExercisePicker from './ExercisePicker';

export default function OnboardingScreen({ onComplete, onCancel }) {
  const [screen, setScreen] = useState("pick"); // "pick" | "customize" | "weights"
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [exercises, setExercises] = useState({});
  const [weights, setWeights] = useState({});
  const [pickerDay, setPickerDay] = useState(null);
  const [expandedEx, setExpandedEx] = useState(null);

  // Custom split builder state
  const DAY_COLORS = ["#3b82f6","#8b5cf6","#f59e0b","#10b981","#ef4444","#ec4899","#06b6d4"];
  const [customDays, setCustomDays] = useState([
    { name: "Push", color: "#3b82f6", dow: 1 },
    { name: "Pull", color: "#10b981", dow: 3 },
    { name: "Legs", color: "#ef4444", dow: 5 },
  ]);

  function pickPreset(preset) {
    setSelectedPreset(preset);
    // Deep copy exercises
    const exCopy = {};
    Object.entries(preset.exercises).forEach(([k, v]) => {
      exCopy[parseInt(k)] = v.map(e => ({ ...e }));
    });
    setExercises(exCopy);
  }

  function useDefault() {
    pickPreset(PRESET_SPLITS[0]);
    finish(PRESET_SPLITS[0], {});
  }

  function goCustomize() {
    setScreen("customize");
  }

  function removeExercise(dayIdx, exId) {
    setExercises(prev => ({ ...prev, [dayIdx]: prev[dayIdx].filter(e => e.id !== exId) }));
  }

  function finish(preset, startingWeights) {
    let dayStructure, finalExercises;

    if (preset) {
      dayStructure = preset.dayStructure;
      finalExercises = exercises;
    } else if (selectedPreset) {
      dayStructure = selectedPreset.dayStructure;
      finalExercises = exercises;
    } else {
      dayStructure = Array.from({ length: 7 }, (_, i) => {
        const td = customDays.find(d => d.dow === i);
        return td ? { name: td.name, color: td.color, isRest: false } : { name: "Rest", color: null, isRest: true };
      });
      finalExercises = exercises;
    }

    // Ensure every day has an explicit entry — prevents DEFAULT_PROGRAM fallback
    const fullProgram = {};
    for (let i = 0; i < 7; i++) {
      fullProgram[i] = finalExercises[i] || [];
    }

    onComplete({ dayStructure, customProgram: fullProgram, startingWeights });
  }

  function startCustom() {
    // Build empty exercises for each custom day
    const exMap = {};
    customDays.forEach(d => { exMap[d.dow] = []; });
    setExercises(exMap);
    setScreen("customize");
    setSelectedPreset(null);
  }

  function addCustomDay() {
    if (customDays.length >= 6) return;
    const usedDows = customDays.map(d => d.dow);
    const freeDow = [1,2,3,4,5,6,0].find(d => !usedDows.includes(d)) ?? 0;
    const usedColors = customDays.map(d => d.color);
    const freeColor = DAY_COLORS.find(c => !usedColors.includes(c)) || DAY_COLORS[0];
    setCustomDays(prev => [...prev, { name: "Day", color: freeColor, dow: freeDow }]);
  }

  function removeCustomDay(idx) {
    setCustomDays(prev => prev.filter((_, i) => i !== idx));
  }

  function updateCustomDay(idx, key, val) {
    setCustomDays(prev => prev.map((d, i) => i === idx ? { ...d, [key]: val } : d));
  }

  const [pickMode, setPickMode] = useState("presets"); // "presets" | "custom"

  function goWeights() {
    setScreen("weights");
  }

  const allExercises = Object.values(exercises).flat();

  // ── PICK SCREEN ──────────────────────────────
  if (screen === "pick") return (
    <div style={{ position: "fixed", inset: 0, background: "var(--bg-0)", zIndex: 100, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <style>{CSS}</style>

      {/* Header */}
      <div style={{ padding: "max(40px, env(safe-area-inset-top)) 20px 0", flexShrink: 0 }}>
        {onCancel && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
            <button onClick={onCancel} style={{
              background: "var(--bg-2)", border: "1px solid rgba(255,255,255,0.08)",
              color: "var(--text-2)", fontSize: 13, fontWeight: 700, cursor: "pointer",
              fontFamily: "inherit", padding: "7px 14px", borderRadius: 20,
            }}>Cancel</button>
          </div>
        )}
        <div style={{ fontSize: 26, fontWeight: 800, color: "var(--text-1)", marginBottom: 2 }}>{onCancel ? "Change Split" : "Welcome to Gymingify"}</div>
        <div style={{ fontSize: 13, color: "var(--text-3)", marginBottom: 16 }}>Choose how you want to get started</div>

        {/* Segmented control */}
        <div style={{ display: "flex", background: "var(--bg-2)", borderRadius: 12, padding: 3, border: "1px solid rgba(255,255,255,0.06)", marginBottom: 4 }}>
          {[{ id: "presets", label: "Choose a Split" }, { id: "custom", label: "Build Custom" }].map(opt => (
            <button key={opt.id} onClick={() => setPickMode(opt.id)} style={{
              flex: 1, padding: "9px 0", borderRadius: 9, fontSize: 13, fontWeight: 700,
              fontFamily: "inherit", cursor: "pointer", border: "none",
              background: pickMode === opt.id ? "var(--accent)" : "transparent",
              color: pickMode === opt.id ? "white" : "var(--text-3)",
              transition: "all 0.15s",
            }}>{opt.label}</button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: "auto", padding: "12px 20px 120px" }}>
        {pickMode === "presets" ? (
          <>
            {PRESET_SPLITS.map(preset => (
              <button key={preset.id} onClick={() => { pickPreset(preset); setScreen("customize"); }} style={{
                width: "100%", textAlign: "left", padding: 16, borderRadius: 16, marginBottom: 10,
                background: preset.isDefault ? `linear-gradient(145deg, ${preset.color}18, ${preset.color}08)` : "var(--bg-2)",
                border: preset.isDefault ? `1px solid ${preset.color}44` : "1px solid rgba(255,255,255,0.06)",
                cursor: "pointer", fontFamily: "inherit",
              }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 6 }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                      <span style={{ fontSize: 15, fontWeight: 800, color: "var(--text-1)" }}>{preset.name}</span>
                      {preset.isDefault && (
                        <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 6px", borderRadius: 5, background: `${preset.color}22`, color: preset.color }}>RECOMMENDED</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: preset.color }}>{preset.tag}</div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: preset.color }}>{preset.daysPerWeek}</div>
                    <div style={{ fontSize: 9, color: "var(--text-3)", fontWeight: 600 }}>days/week</div>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: "var(--text-2)", lineHeight: 1.5 }}>{preset.description}</div>
                <div style={{ display: "flex", gap: 4, marginTop: 10, flexWrap: "wrap" }}>
                  {preset.dayStructure.filter(d => !d.isRest).map((d, i) => (
                    <span key={i} style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 5, background: `${d.color}18`, color: d.color }}>{d.name}</span>
                  ))}
                </div>
              </button>
            ))}
          </>
        ) : (
          // Custom builder inline
          <>
            <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 14 }}>
              Pick your training days, name them, and choose a color. You'll add exercises on the next step.
            </div>
            {(() => {
              const DAYS_OF_WEEK = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
              return (
                <>
                  {customDays.map((day, idx) => (
                    <div key={idx} style={{ ...s.card, padding: 14, marginBottom: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                        <div style={{ width: 11, height: 11, borderRadius: "50%", background: day.color, flexShrink: 0 }} />
                        <input
                          value={day.name}
                          onChange={e => updateCustomDay(idx, "name", e.target.value)}
                          placeholder="Day name (e.g. Push)"
                          style={{ ...s.inp, flex: 1, fontSize: 14, padding: "6px 10px" }}
                        />
                        <button onClick={() => removeCustomDay(idx)} style={{ width: 28, height: 28, borderRadius: 7, border: "none", background: "rgba(248,113,113,0.08)", color: "var(--red)", fontSize: 13, cursor: "pointer", flexShrink: 0 }}>✕</button>
                      </div>
                      <div style={{ display: "flex", gap: 4, marginBottom: 10, flexWrap: "wrap" }}>
                        {DAYS_OF_WEEK.map((d, i) => {
                          const taken = customDays.some((cd, ci) => ci !== idx && cd.dow === i);
                          const sel = day.dow === i;
                          return (
                            <button key={i} onClick={() => !taken && updateCustomDay(idx, "dow", i)} style={{
                              padding: "4px 10px", borderRadius: 7, fontSize: 11, fontWeight: 700,
                              fontFamily: "inherit", cursor: taken ? "not-allowed" : "pointer",
                              background: sel ? `${day.color}22` : taken ? "rgba(255,255,255,0.02)" : "var(--bg-2)",
                              color: sel ? day.color : taken ? "rgba(255,255,255,0.15)" : "var(--text-2)",
                              border: sel ? `1px solid ${day.color}44` : "1px solid rgba(255,255,255,0.05)",
                            }}>{d}</button>
                          );
                        })}
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        {DAY_COLORS.map(c => (
                          <button key={c} onClick={() => updateCustomDay(idx, "color", c)} style={{
                            width: 22, height: 22, borderRadius: "50%",
                            border: day.color === c ? "2px solid white" : "2px solid transparent",
                            background: c, cursor: "pointer", flexShrink: 0,
                          }} />
                        ))}
                      </div>
                    </div>
                  ))}
                  {customDays.length < 6 && (
                    <button onClick={addCustomDay} style={{
                      width: "100%", padding: "12px 0", borderRadius: 12, fontSize: 13, fontWeight: 700,
                      background: "none", border: "1px dashed rgba(255,255,255,0.12)", color: "var(--text-3)",
                      cursor: "pointer", fontFamily: "inherit", marginBottom: 10,
                    }}>+ Add Training Day</button>
                  )}
                </>
              );
            })()}
          </>
        )}
      </div>

      {/* Bottom bar */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, padding: "12px 20px", paddingBottom: "max(20px, env(safe-area-inset-bottom))", background: "var(--bg-0)", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
        {pickMode === "custom" ? (
          <button onClick={startCustom} style={{ ...s.btnP, width: "100%", padding: "16px 0", fontSize: 15, fontWeight: 700, fontFamily: "inherit", borderRadius: 16 }}>
            Next — Customize Exercises →
          </button>
        ) : (
          <button onClick={useDefault} style={{
            width: "100%", padding: "14px 0", borderRadius: 14, fontSize: 14, fontWeight: 700,
            background: "transparent", color: "var(--text-3)", border: "1px solid rgba(255,255,255,0.07)",
            cursor: "pointer", fontFamily: "inherit",
          }}>Skip — Use Default Split</button>
        )}
      </div>
    </div>
  );


  // ── CUSTOMIZE SCREEN ─────────────────────────
  if (screen === "customize") {
    const trainingDays = selectedPreset
      ? selectedPreset.dayStructure.map((d, i) => ({ ...d, idx: i })).filter(d => !d.isRest)
      : customDays.map(d => ({ name: d.name, color: d.color, isRest: false, idx: d.dow }));

    function updateExercise(dayIdx, exId, key, val) {
      setExercises(prev => ({
        ...prev,
        [dayIdx]: prev[dayIdx].map(e => e.id === exId ? { ...e, [key]: val } : e),
      }));
    }

    return (
      <>
        <div style={{ position: "fixed", inset: 0, background: "var(--bg-0)", zIndex: 100, display: "flex", flexDirection: "column" }}>
        <style>{CSS}</style>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", paddingTop: "max(12px, env(safe-area-inset-top))", borderBottom: "1px solid rgba(255,255,255,0.05)", flexShrink: 0 }}>
          <button onClick={() => setScreen("pick")} style={{ ...s.btnG, padding: "8px 14px", fontSize: 13 }}>‹ Back</button>
          <span style={{ fontSize: 15, fontWeight: 800 }}>Customize Exercises</span>
          <button onClick={goWeights} style={{ ...s.btnP, padding: "8px 14px", fontSize: 13 }}>Next →</button>
        </div>

        <div style={{ fontSize: 12, color: "var(--text-3)", padding: "10px 20px", borderBottom: "1px solid rgba(255,255,255,0.03)", flexShrink: 0 }}>
          Tap any exercise to edit sets and reps. Remove or add exercises per day.
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: "12px 20px 100px" }}>
          {trainingDays.map(day => (
            <div key={day.idx} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: day.color, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>{day.name}</div>
              {(exercises[day.idx] || []).map(ex => {
                const exKey = `${day.idx}-${ex.id}`;
                const isExpanded = expandedEx === exKey;
                return (
                  <div key={ex.id} style={{ borderRadius: 12, marginBottom: 4, background: "var(--bg-2)", border: `1px solid ${isExpanded ? day.color + "44" : "rgba(255,255,255,0.04)"}`, overflow: "hidden" }}>
                    {/* Row */}
                    <div style={{ display: "flex", alignItems: "center", padding: "10px 12px" }}>
                      <button onClick={() => setExpandedEx(isExpanded ? null : exKey)} style={{ flex: 1, textAlign: "left", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-1)" }}>{ex.name}</div>
                        <div style={{ fontSize: 11, color: isExpanded ? day.color : "var(--text-3)", marginTop: 1 }}>
                          {ex.sets} sets · {ex.rLo}{ex.rHi !== ex.rLo ? `–${ex.rHi}` : ""} reps · {ex.compound ? "Compound" : "Isolation"}
                        </div>
                      </button>
                      <span style={{ fontSize: 12, color: "var(--text-3)", marginRight: 8 }}>{isExpanded ? "▲" : "▼"}</span>
                      <button onClick={() => { removeExercise(day.idx, ex.id); setExpandedEx(null); }} style={{ width: 28, height: 28, borderRadius: 7, border: "none", background: "rgba(248,113,113,0.08)", color: "var(--red)", fontSize: 14, cursor: "pointer" }}>✕</button>
                    </div>

                    {/* Expanded editor */}
                    {isExpanded && (
                      <div style={{ padding: "0 12px 12px", borderTop: `1px solid ${day.color}22` }}>
                        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ ...s.label, marginBottom: 4 }}>SETS</div>
                            <input type="number" inputMode="numeric" value={ex.sets}
                              onChange={e => updateExercise(day.idx, ex.id, "sets", parseInt(e.target.value) || 3)}
                              style={{ ...s.inp, textAlign: "center", padding: "7px 0", fontSize: 15 }} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ ...s.label, marginBottom: 4 }}>REP MIN</div>
                            <input type="number" inputMode="numeric" value={ex.rLo}
                              onChange={e => updateExercise(day.idx, ex.id, "rLo", parseInt(e.target.value) || 1)}
                              style={{ ...s.inp, textAlign: "center", padding: "7px 0", fontSize: 15 }} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ ...s.label, marginBottom: 4 }}>REP MAX</div>
                            <input type="number" inputMode="numeric" value={ex.rHi}
                              onChange={e => updateExercise(day.idx, ex.id, "rHi", parseInt(e.target.value) || 1)}
                              style={{ ...s.inp, textAlign: "center", padding: "7px 0", fontSize: 15 }} />
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                          <button onClick={() => updateExercise(day.idx, ex.id, "compound", true)} style={{
                            flex: 1, padding: "8px 0", borderRadius: 10, fontSize: 12, fontWeight: 700,
                            border: "none", cursor: "pointer", fontFamily: "inherit",
                            background: ex.compound ? `${day.color}20` : "var(--bg-3)",
                            color: ex.compound ? day.color : "var(--text-3)",
                          }}>Compound</button>
                          <button onClick={() => updateExercise(day.idx, ex.id, "compound", false)} style={{
                            flex: 1, padding: "8px 0", borderRadius: 10, fontSize: 12, fontWeight: 700,
                            border: "none", cursor: "pointer", fontFamily: "inherit",
                            background: !ex.compound ? `${day.color}20` : "var(--bg-3)",
                            color: !ex.compound ? day.color : "var(--text-3)",
                          }}>Isolation</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              <button onClick={() => setPickerDay(day.idx)} style={{
                width: "100%", padding: "8px 0", borderRadius: 10, fontSize: 12, fontWeight: 700,
                background: "none", border: `1px dashed ${day.color}40`, color: day.color,
                cursor: "pointer", fontFamily: "inherit", marginTop: 4,
              }}>+ Add Exercise</button>
            </div>
          ))}
        </div>
      </div>

      {pickerDay !== null && (
        <ExercisePicker
          dayColor={trainingDays.find(d => d.idx === pickerDay)?.color}
          onClose={() => setPickerDay(null)}
          onSelect={ex => {
            setExercises(prev => ({ ...prev, [pickerDay]: [...(prev[pickerDay] || []), { ...ex }] }));
            setPickerDay(null);
          }}
        />
      )}
      </>
    );
  }

  // ── STARTING WEIGHTS SCREEN ───────────────────
  if (screen === "weights") return (
    <div style={{ position: "fixed", inset: 0, background: "var(--bg-0)", zIndex: 100, display: "flex", flexDirection: "column" }}>
      <style>{CSS}</style>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", paddingTop: "max(12px, env(safe-area-inset-top))", borderBottom: "1px solid rgba(255,255,255,0.05)", flexShrink: 0 }}>
        <button onClick={() => setScreen("customize")} style={{ ...s.btnG, padding: "8px 14px", fontSize: 13 }}>‹ Back</button>
        <span style={{ fontSize: 15, fontWeight: 800 }}>Starting Weights</span>
        <button onClick={() => finish(null, weights)} style={{ ...s.btnP, padding: "8px 14px", fontSize: 13 }}>Done →</button>
      </div>

      <div style={{ fontSize: 12, color: "var(--text-3)", padding: "10px 20px", borderBottom: "1px solid rgba(255,255,255,0.03)", flexShrink: 0 }}>
        Optional — enter your current working weights so the app can make suggestions from day one. Leave blank to skip any exercise.
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "12px 20px 120px" }}>
        {allExercises.filter((ex, i, arr) => arr.findIndex(e => e.id === ex.id) === i).map(ex => (
          <div key={ex.id} style={{ display: "flex", alignItems: "center", padding: "10px 12px", borderRadius: 12, marginBottom: 6, background: "var(--bg-2)", border: "1px solid rgba(255,255,255,0.04)" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-1)" }}>{ex.name}</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="number" inputMode="decimal"
                value={weights[ex.id] || ""}
                onChange={e => setWeights(prev => ({ ...prev, [ex.id]: parseFloat(e.target.value) || undefined }))}
                placeholder="lbs"
                style={{ ...s.inp, width: 80, textAlign: "center", fontSize: 14, padding: "6px 8px" }}
              />
            </div>
          </div>
        ))}
      </div>

      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, padding: "12px 20px", paddingBottom: "max(20px, env(safe-area-inset-bottom))", background: "var(--bg-0)", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
        <button onClick={() => finish(null, weights)} style={{ ...s.btnP, width: "100%", padding: "16px 0", fontSize: 15, fontWeight: 700, fontFamily: "inherit", borderRadius: 16 }}>
          Start Training →
        </button>
        <button onClick={() => finish(null, {})} style={{ width: "100%", padding: "10px 0", fontSize: 13, color: "var(--text-3)", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", marginTop: 4 }}>
          Skip — I'll enter weights as I go
        </button>
      </div>
    </div>
  );

  return null;
}
